import { Button, Modal, Popconfirm, Tag, notification } from "antd";
import { UsersIcon, PencilSimpleIcon, TrashIcon, CalendarIcon, UserIcon, UserPlusIcon } from "@phosphor-icons/react";
import SidebarLayout from "components/Layout/Layout";
import dayjs from "dayjs";
import NewRoleModalContent from "pages/Role/AddNewRoleGroup";
import AddUsersToRoleInterface from "pages/Role/AddUserToRole";
import { useState, useEffect } from "react";
import { API_URL } from "env";
import PermissionEdit from "./EditRole";

interface PermissionPivot {
    role_id: number;
    permission_id: number;
    created_at: string;
    updated_at: string;
};

interface Permission {
    id: number;
    name: string;
    display_name: string;
    module: string;
    action: string;
    pivot: PermissionPivot;
};

interface PermissionDisplay {
    id: number;
    name: string;
    display_name: string;
    module: string;
    action: string;
};

interface RoleItem {
    id: number;
    name: string;
    display_name: string;
    description: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
    permissions: Permission[];
    permissions_count: number;
    users_count: number;
    permissions_display: PermissionDisplay[];
};

interface RoleApiData {
    total: number;
    current_page: number;
    items: RoleItem[];
};

interface RoleApiResponse {
    status: boolean;
    data: RoleApiData;
};

export type { RoleApiResponse, RoleItem, Permission, PermissionDisplay, PermissionPivot };

const RoleMainPage = () => {
    const [roleData, setRoleData] = useState<RoleItem[]>([]);
    const [permission, setPermission] = useState<Permission[]>([]);
    const [addRoleModal, setAddRoleModal] = useState(false);

    const fetchPermission = async () => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json();
            if (data.status) setPermission(data.data.items);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const fetchRoleData = async () => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/roles`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json();
            if (data.status) setRoleData(data.data.items);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const refresh = () => { fetchRoleData(); fetchPermission(); };

    useEffect(() => { refresh(); }, []);

    return (
        <SidebarLayout>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold m-0">Roles</h2>
                    <Button type="primary" onClick={() => setAddRoleModal(true)}>
                        + Add New Role
                    </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {roleData.map(role => (
                        <RoleCard key={role.id} role={role} permission={permission} onUpdate={refresh} />
                    ))}
                </div>
            </div>

            <Modal
                title="Add New Role"
                open={addRoleModal}
                onCancel={() => setAddRoleModal(false)}
                style={{ top: 50 }}
                width={1000}
                footer={null}
            >
                <NewRoleModalContent onClose={() => { setAddRoleModal(false); refresh(); }} />
            </Modal>
        </SidebarLayout>
    );
};

const RoleCard = ({ role, permission, onUpdate }: { role: RoleItem; permission: Permission[]; onUpdate: () => void }) => {
    const [editRoleModal, setEditRoleModal] = useState(false);
    const [manageUsersModal, setManageUsersModal] = useState(false);
    const [api, contextHolder] = notification.useNotification();

    const deleteRole = async (roleId: number) => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/${roleId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json();
            if (data.status) {
                onUpdate();
            } else {
                api.error({ message: 'Error', description: typeof data.error === 'string' ? data.error : JSON.stringify(data.error), placement: 'topRight' });
            }
        } catch (error) {
            console.error('Error deleting role:', error);
        }
    };

    return (
        <>
            {contextHolder}
            <div className="bg-bg-card border border-border rounded-xl p-5 shadow-card flex flex-col justify-between gap-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 shrink-0">
                            <UsersIcon size={20} className="text-blue-500" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-sm m-0">{role.display_name}</h3>
                            <p className="text-xs text-text-muted m-0">{role.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setEditRoleModal(true)}
                            className="p-1.5 rounded hover:bg-bg-hover transition-colors cursor-pointer text-text-secondary hover:text-blue-500"
                            title="Edit"
                        >
                            <PencilSimpleIcon size={15} />
                        </button>
                        <Popconfirm
                            title={`Delete role "${role.display_name}"?`}
                            description="This action cannot be undone."
                            onConfirm={() => deleteRole(role.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <button
                                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors cursor-pointer text-text-secondary hover:text-red-500"
                                title="Delete"
                            >
                                <TrashIcon size={15} />
                            </button>
                        </Popconfirm>
                    </div>
                </div>

                {/* Meta */}
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-text-secondary">
                        <CalendarIcon size={13} className="text-text-muted shrink-0" />
                        <span>Updated {dayjs(role.updated_at).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-secondary">
                        <UserIcon size={13} className="text-text-muted shrink-0" />
                        <span>{role.users_count} members</span>
                    </div>
                </div>

                {/* Permissions */}
                <div>
                    <p className="text-xs font-medium text-text-secondary mb-1.5">Permissions</p>
                    <div className="flex flex-wrap gap-1">
                        {role.permissions_display?.slice(0, 7).map(perm => (
                            <Tag key={perm.id} className="text-xs m-0">{perm.display_name}</Tag>
                        ))}
                        {role.permissions_display?.length > 7 && (
                            <Tag className="text-xs m-0">+{role.permissions_display.length - 7} more</Tag>
                        )}
                        {(!role.permissions_display || role.permissions_display.length === 0) && (
                            <span className="text-xs text-text-muted">No permissions</span>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="pt-3 border-t border-border">
                    <button
                        onClick={() => setManageUsersModal(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors cursor-pointer"
                    >
                        <UserPlusIcon size={15} />
                        Manage Members
                    </button>
                </div>
            </div>

            <Modal
                title={`Edit Role: ${role.display_name}`}
                open={editRoleModal}
                onCancel={() => setEditRoleModal(false)}
                style={{ top: 50 }}
                width={1000}
                footer={null}
            >
                <PermissionEdit permission={permission} role={role} onClose={() => { onUpdate(); setEditRoleModal(false); }} />
            </Modal>

            <Modal
                title={`Manage Members: ${role.display_name}`}
                open={manageUsersModal}
                onCancel={() => setManageUsersModal(false)}
                style={{ top: 50 }}
                width={1000}
                footer={null}
            >
                <AddUsersToRoleInterface role={role} onClose={() => { onUpdate(); setManageUsersModal(false); }} />
            </Modal>
        </>
    );
};

export default RoleMainPage;
